import { ref } from "vue";
import { PlacePrediction } from "@/types/calculators";
import AutocompleteSuggestion = google.maps.places.AutocompleteSuggestion;
import { setOptions } from "@googlemaps/js-api-loader";

export function useGoogleAutocomplete(
  language = "ru",
  type: "city" | "address" = "city"
) {
  const options = ref<PlacePrediction[]>([]);
  const loading = ref(false);
  const autocompleteSuggestion = ref<typeof AutocompleteSuggestion>();
  const isAutocompleteSuggestionLoaded = ref(false);
  const geocoder = ref<google.maps.Geocoder | null>(null);
  const isGeocoderLoaded = ref(false);

  function initGoogleObject() {
    if (!window.google) {
      setOptions({
        key: process.env.VUE_APP_GOOGLE_MAP_KEY,
        v: "weekly",
      });
    }
  }

  async function fetchSuggestions(query: string) {
    options.value = [];
    loading.value = true;
    try {
      const autocompleteApi = await getAutocompleteSuggestion();

      const searchType = type === "city" ? ["(cities)"] : ["street_address"];
      const request = {
        input: query,
        includedPrimaryTypes: searchType,
        language,
        sessionToken: new google.maps.places.AutocompleteSessionToken(),
      };

      const { suggestions } =
        await autocompleteApi.fetchAutocompleteSuggestions(request);

      for (const suggestion of suggestions) {
        const placePrediction = suggestion.placePrediction;
        if (placePrediction) {
          options.value.push({
            label: placePrediction.text.toString(),
            city: placePrediction.mainText?.toString() ?? null,
            place: placePrediction.toPlace(),
          });
        }
      }
    } catch (error) {
      console.error("Ошибка при получении подсказок:", error);
    } finally {
      loading.value = false;
    }
  }

  function normalizeGoogleTimeZone(timeZone: string): string {
    const deprecatedMap: Record<string, string> = {
      "Europe/Kiev": "Europe/Kyiv",
      "Asia/Calcutta": "Asia/Kolkata",
      "Asia/Katmandu": "Asia/Kathmandu",
      "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
      "America/Catamarca": "America/Argentina/Catamarca",
      "America/Cordoba": "America/Argentina/Cordoba",
      "America/Jujuy": "America/Argentina/Jujuy",
      "America/Mendoza": "America/Argentina/Mendoza",
      "America/Rosario": "America/Argentina/Cordoba",
      "Asia/Saigon": "Asia/Ho_Chi_Minh",
      "Asia/Ujung_Pandang": "Asia/Makassar",
      "Asia/Tel_Aviv": "Asia/Jerusalem",
      "Asia/Chongqing": "Asia/Shanghai",
      "Asia/Harbin": "Asia/Shanghai",
      "Asia/Kashgar": "Asia/Urumqi",
      "Pacific/Truk": "Pacific/Chuuk",
      "Pacific/Yap": "Pacific/Chuuk",
      "Pacific/Ponape": "Pacific/Pohnpei",
      "Pacific/Ponhpei": "Pacific/Pohnpei",
      "Pacific/Samoa": "Pacific/Pago_Pago",
      "US/Eastern": "America/New_York",
      "US/Central": "America/Chicago",
      "US/Mountain": "America/Denver",
      "US/Pacific": "America/Los_Angeles",
      "Canada/Eastern": "America/Toronto",
      "Canada/Central": "America/Winnipeg",
      "Canada/Mountain": "America/Edmonton",
      "Canada/Pacific": "America/Vancouver",
      Singapore: "Asia/Singapore",
      "Arctic/Longyearbyen": "Europe/Oslo",
    };

    return deprecatedMap[timeZone] || timeZone;
  }

  async function getAutocompleteSuggestion() {
    initGoogleObject();
    if (autocompleteSuggestion.value) {
      return autocompleteSuggestion.value;
    }
    if (isAutocompleteSuggestionLoaded.value) {
      throw new Error("Can't load autocomplete suggestion");
    }
    const { AutocompleteSuggestion } = (await google.maps.importLibrary(
      "places"
    )) as google.maps.PlacesLibrary;

    autocompleteSuggestion.value = AutocompleteSuggestion;
    isAutocompleteSuggestionLoaded.value = true;

    return autocompleteSuggestion.value;
  }

  async function getGeocoder(): Promise<google.maps.Geocoder | null> {
    initGoogleObject();
    if (geocoder.value) {
      return geocoder.value;
    }
    if (isGeocoderLoaded.value) {
      return null;
    }
    try {
      await google.maps.importLibrary("geocoding");

      geocoder.value = new google.maps.Geocoder();
      isGeocoderLoaded.value = true;

      return geocoder.value;
    } catch (e) {
      console.log("Can't init geocoder");
      isGeocoderLoaded.value = true; // only once try to init geocoder
      return null;
    }
  }

  return {
    options,
    fetchSuggestions,
    normalizeGoogleTimeZone,
    loading,
    getGeocoder,
  };
}
